'use client'

import { FC } from 'react'
import { testIds, testIdHelpers } from '@/root/shared-consts'
import type { PaymentMethod } from '@/root/shared-types/reservation/reservation-types'
import cn from 'classnames'
import { useGetTranslations } from '@/root/shared-hooks'
import { BookTicketIcon } from '@/root/ui/shared/icons/BookTicketIcon'
import { CreditPaymentButton } from '@/root/components/tuuleliinid/CreditPaymentButton/CreditPaymentButton'

interface PaymentMethodsProps {
  /** Already enabled-only and in display order */
  paymentMethods: PaymentMethod[]
  selectedMethod?: string
  onSelect: (method: PaymentMethod) => void
  creditBalance: number
  isCreditInsufficient: boolean
}

export const PaymentMethods: FC<PaymentMethodsProps> = ({
  paymentMethods,
  selectedMethod,
  onSelect,
  creditBalance,
  isCreditInsufficient,
}) => {
  const { getTranslation } = useGetTranslations()

  return (
    <>
      <div className="relative text-2xl font-bold">{getTranslation('paymentMethodsTitle')}</div>
      <div
        data-testid={testIds.confirm.paymentMethods}
        className="flex w-full flex-wrap items-start justify-start gap-5"
      >
        {paymentMethods.map((method) =>
          method.code === 'CREDIT' ? (
            <CreditPaymentButton
              key={method.code}
              testId={testIdHelpers.paymentMethod(method.code)}
              creditBalance={creditBalance}
              selected={selectedMethod === method.code}
              onSelect={() => onSelect(method)}
              disabled={isCreditInsufficient}
            />
          ) : (
            <button
              key={method.code}
              type="button"
              data-testid={testIdHelpers.paymentMethod(method.code)}
              onClick={() => onSelect(method)}
              className={cn(
                'flex h-30 w-45 flex-col items-center justify-center gap-2.5 rounded-2xl border py-4',
                selectedMethod === method.code
                  ? 'border-success bg-success-very-light border-2'
                  : 'border-neutral-medium-lighter hover:border-neutral-medium bg-white'
              )}
            >
              <div className="flex h-10 items-center justify-center">
                {method.logoUrl ? (
                  <img src={method.logoUrl} alt={method.title} className="max-h-10 max-w-25 object-contain" />
                ) : (
                  // A method without a logo (f-edge drops a null logoUrl, e.g. BOOKED) gets a neutral icon
                  <BookTicketIcon />
                )}
              </div>
              <span className="text-neutral-darker w-full px-4 text-center text-sm leading-tight">{method.title}</span>
            </button>
          )
        )}
      </div>
    </>
  )
}
